import { useState } from 'react';
import { Box, Collapse, LinearProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import dayjs from 'dayjs';
import { Icon } from '@iconify/react/dist/iconify.js';
import { EmptyState, IconBadge, SoftChip } from '../components/ui';
import { RT } from '../theme/retentionTheme';

// Stage 9 — what the tutor already recorded in the old tables (GET /retention/:id/tracking → mid.subjects):
// sessions held from trainer_attendance, every student_examination with its criteria marks, and the lessons logged
// per session. Read-only; the form below is prefilled from it.

const KIND_COLOR = { pre: RT.info, exam: '#7C3AED', post: RT.success };
const pct = (got, total) => (Number(total) > 0 ? Math.round((Number(got) * 100) / Number(total)) : null);
const num = (v) => (v === null || v === undefined ? '—' : String(Number(v)));

// student_examination ratings are out of 10
const RATINGS = [
  ['student_engagement_rating', 'Engagement'],
  ['punctuality_rating', 'Punctuality'],
  ['attendance_rating', 'Attendance'],
  ['handwriting_rating', 'Handwriting'],
  ['extra_curricular_activities_rating', 'Extra-curricular'],
];

function Bar({ value, color }) {
  return (
    <LinearProgress
      variant="determinate"
      value={Math.max(0, Math.min(100, value ?? 0))}
      sx={{ height: 6, borderRadius: 3, bgcolor: alpha(color, 0.12), '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 } }}
    />
  );
}

function CriteriaRow({ c, sub }) {
  const p = pct(c.marks_obtained, c.total_marks);
  return (
    <Stack direction="row" alignItems="center" gap={1.25} sx={{ pl: sub ? 2.5 : 0 }}>
      <Typography variant={sub ? 'caption' : 'body2'} sx={{ flex: 1, minWidth: 0, color: sub ? RT.muted : RT.ink, fontWeight: sub ? 500 : 600 }} noWrap title={c.criteria_name}>
        {c.criteria_name || '—'}
      </Typography>
      {p !== null ? (
        <>
          <Box sx={{ width: 90 }}>
            <Bar value={p} color={sub ? RT.faint : RT.primary} />
          </Box>
          <Typography variant="caption" sx={{ width: 56, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {num(c.marks_obtained)}/{num(c.total_marks)}
          </Typography>
        </>
      ) : (
        <Box sx={{ width: 146 }} />
      )}
    </Stack>
  );
}

function ExamRow({ exam, stored }) {
  const [open, setOpen] = useState(false);
  const color = KIND_COLOR[exam.kind] || RT.primary;
  const p = pct(exam.marks_obtained, exam.total_marks);
  const ratings = RATINGS.filter(([k]) => exam[k]);
  const hasMore = exam.criteria?.length > 0 || ratings.length > 0 || exam.tutor_suggestions;
  return (
    <Box sx={{ borderRadius: 2.5, border: `1px solid ${open ? alpha(color, 0.4) : RT.line}`, bgcolor: RT.paper }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.25}
        onClick={() => hasMore && setOpen((o) => !o)}
        sx={{ p: 1.1, cursor: hasMore ? 'pointer' : 'default' }}
      >
        <SoftChip label={exam.type_name} color={color} sx={{ minWidth: 112 }} />
        {stored && <Icon icon="solar:diskette-bold" width={14} color={RT.success} title="Stored with the report" />}
        <Typography variant="caption" sx={{ color: RT.muted, width: 78 }}>
          {exam.examination_date ? dayjs(exam.examination_date).format('DD MMM YY') : '—'}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <Bar value={p} color={color} />
        </Box>
        <Typography variant="subtitle2" sx={{ width: 76, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {num(exam.marks_obtained)}/{num(exam.total_marks)}
        </Typography>
        <Typography variant="caption" sx={{ width: 38, textAlign: 'right', color: RT.muted, fontWeight: 700 }}>
          {p !== null ? `${p}%` : ''}
        </Typography>
        <Icon icon={open ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-down-linear'} width={16} color={hasMore ? RT.faint : 'transparent'} />
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Stack gap={1.25} sx={{ px: 1.5, pb: 1.5, pt: 0.5, borderTop: `1px dashed ${RT.line}` }}>
          {exam.criteria?.length > 0 && (
            <Stack gap={0.6}>
              {exam.criteria.map((c) => (
                <Box key={c.criteria_id}>
                  <CriteriaRow c={c} />
                  {c.sub?.map((s) => (
                    <CriteriaRow key={s.criteria_id} c={s} sub />
                  ))}
                </Box>
              ))}
            </Stack>
          )}
          {ratings.length > 0 && (
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {ratings.map(([k, l]) => (
                <SoftChip key={k} label={`${l} · ${exam[k]}/10`} color={RT.ink2} />
              ))}
            </Stack>
          )}
          {exam.tutor_suggestions && (
            <Typography variant="body2" sx={{ color: RT.ink2, whiteSpace: 'pre-line' }}>
              <b>Tutor:</b> {exam.tutor_suggestions}
            </Typography>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}

function SubjectBlock({ s, storedIds }) {
  const held = pct(s.sessions_held, s.total_sessions);
  const pre = s.pre_exam;
  const mid = s.mid_exam;
  const gain = pre && mid ? (pct(mid.marks_obtained, mid.total_marks) ?? 0) - (pct(pre.marks_obtained, pre.total_marks) ?? 0) : null;
  return (
    <Stack gap={1.25}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <IconBadge icon="solar:book-2-bold-duotone" color={RT.primary} size={30} />
        <Typography variant="subtitle1" sx={{ flex: 1, minWidth: 120 }} noWrap>
          {s.subject_name || 'Subject'}
          {s.tutor_name && (
            <Typography component="span" variant="caption" sx={{ color: RT.muted, ml: 1 }}>
              {s.tutor_name}
            </Typography>
          )}
        </Typography>
        <SoftChip
          label={`${s.sessions_held}${s.total_sessions ? ` / ${s.total_sessions}` : ''} sessions held`}
          icon="solar:calendar-mark-bold"
          color={RT.info}
        />
        {gain !== null && (
          <SoftChip
            label={`${gain >= 0 ? '+' : ''}${gain}% since pre-assessment`}
            icon={gain >= 0 ? 'solar:graph-up-bold' : 'solar:graph-down-bold'}
            color={gain >= 0 ? RT.success : RT.error}
          />
        )}
      </Stack>
      {held !== null && <Bar value={held} color={RT.info} />}

      {s.exams.length ? (
        <Stack gap={0.75}>
          {s.exams.map((e) => (
            <ExamRow key={e.student_examination_id} exam={e} stored={storedIds.has(e.student_examination_id)} />
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" sx={{ color: RT.faint }}>
          No assessments entered by the tutor yet
        </Typography>
      )}

      {s.recent_lessons?.length > 0 && (
        <Box>
          <Typography variant="overline" sx={{ color: RT.muted }}>
            Recent lessons
          </Typography>
          <Stack direction="row" gap={0.6} flexWrap="wrap" mt={0.25}>
            {s.recent_lessons.map((l) => (
              <SoftChip
                key={`${l.attendance_date}-${l.lessons}`}
                label={`${dayjs(l.attendance_date).format('DD MMM')} · ${l.lessons}`}
                color={RT.ink2}
                sx={{ maxWidth: 320 }}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

const KIND_NAME = { 1: 'pre', 2: 'mid', 3: 'post' };

// stored: the exams the saved report holds (tracking → reports[type].exams, retention_report_exam)
export default function MidProgressSources({ subjects = [], stored }) {
  const storedIds = new Set((stored || []).map((x) => x.student_examination_id));
  const counts = (stored || []).reduce((acc, x) => ({ ...acc, [x.exam_kind]: (acc[x.exam_kind] || 0) + 1 }), {});
  const storedLabel = Object.keys(counts).sort().map((k) => `${counts[k]} ${KIND_NAME[k]}`).join(' · ');
  return (
    <Box sx={{ p: 1.75, borderRadius: 3, bgcolor: '#F5F7FF', border: `1px solid ${RT.line}` }}>
      <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
        <IconBadge icon="solar:square-academic-cap-bold-duotone" color={RT.primary} size={28} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          From the tutor
        </Typography>
        {stored ? (
          <SoftChip
            label={stored.length ? `${stored.length} exams stored · ${storedLabel}` : 'No exams stored yet'}
            icon="solar:diskette-bold"
            color={stored.length ? RT.success : RT.muted}
            sx={{ height: 20, fontSize: 10.5 }}
          />
        ) : (
          <SoftChip label="not saved yet" icon="solar:diskette-linear" color={RT.muted} sx={{ height: 20, fontSize: 10.5 }} />
        )}
      </Stack>
      {subjects.length === 0 ? (
        <EmptyState icon="solar:book-2-linear" title="No active subjects" compact />
      ) : (
        <Stack gap={2.25} divider={<Box sx={{ borderTop: `1px dashed ${RT.line}` }} />}>
          {subjects.map((s) => (
            <SubjectBlock key={s.subject_enrollment_id} s={s} storedIds={storedIds} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
